import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  approveRedeem,
  getAllRedeem,
} from "../../../services/billing-invoice/redeem";
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";

import SectionHeader from "../../../components/section/SectionHeader";
import SectionTitle from "../../../components/section/SectionTitle";
import InvoiceRedeemTable from "../../../components/billing-invoice-redeem/InvoiceRedeemTable";
import PaginatedView from "../../../components/pagination/PaginatedView";
import SearchView from "../../../components/search/SearchView";

import { paginationOffset } from "../../../helpers/pagination";
import { formatAppDate } from "../../../helpers/date-formatter";

const path = "invoice/redeem";

function InvoiceRedeem() {
  const [redeems, setRedeems] = useState([]);
  const [redeemResponse, setRedeemResponse] = useState(null);

  const { page } = useParams();
  const [searchParams] = useSearchParams();

  const location = useLocation();
  const navigate = useNavigate();

  async function fetchRedeems() {
    try {
      const search = searchParams.get("search");
      const start = searchParams.get("start");
      const end = searchParams.get("end");
      const sortBy = searchParams.get("sortBy");
      const order = searchParams.get("order");

      const response = await getAllRedeem(
        page,
        search,
        start,
        end,
        sortBy,
        order
      );
      setRedeemResponse(response);
      setRedeems(response.data);
    } catch (err) {
      console.log(err);
    }
  }

  useEffect(() => {
    fetchRedeems();
  }, [page, searchParams]);

  const handleToggleApprove = async (id) => {
    try {
      const response = await approveRedeem(id);
      if (response) {
        fetchRedeems();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const existingSearchParams = useCallback(
    (excluding = []) => {
      const params = [];

      searchParams.forEach((value, key) => {
        if (excluding.indexOf(key) === -1) {
          params.push(key + "=" + value);
        }
      });

      return params;
    },
    [searchParams, navigate]
  );

  const handleSearch = useCallback(
    (text, start, end) => {
      let params = [];
      if (text !== "") {
        params.push(`search=${text}`);
      }

      if (start !== null) {
        params.push(`start=${formatAppDate(start)}`);
      }

      if (end !== null) {
        params.push(`end=${formatAppDate(end)}`);
      }

      // const existingParams = existingSearchParams(["search", "start", "end"]);

      // params = [...params, ...existingParams];

      if (params.length > 0) {
        navigate(`/${path}?${params.join("&")}`);
      }
    },
    [navigate, existingSearchParams, location]
  );

  const handleSorting = useCallback(
    (sortBy) => {
      let sortOrder = searchParams.get("order");
      if (sortOrder == null || sortOrder === "DESC") {
        sortOrder = "ASC";
      } else {
        sortOrder = "DESC";
      }

      let params = [];
      if (sortBy != null && sortBy.trim() !== "") {
        params.push(`sortBy=${sortBy}`);
      }
      params.push(`order=${sortOrder}`);

      const existingParams = existingSearchParams(["sortBy", "order"]);

      params = [...existingParams, ...params];

      navigate(`${location.pathname}?${params.join("&")}`);
    },
    [navigate, existingSearchParams, location]
  );

  const queryParams = useMemo(() => {
    const params = existingSearchParams();

    return params.length > 0 ? params.join("&") : "";
  }, [existingSearchParams]);

  const searchText = useMemo(
    () => searchParams.get("search") ?? "",
    [searchParams, location]
  );

  const startDate = useMemo(
    () => searchParams.get("start"),
    [searchParams, location]
  );

  const endDate = useMemo(
    () => searchParams.get("end"),
    [searchParams, location]
  );

  return (
    <>
      <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white p-2">
        <div className="rounded-t mb-0 px-4 py-3 pb-8 border-0">
          <SectionHeader>
            <SectionTitle title={"Bonus Redeems"} />
          </SectionHeader>

          <SearchView
            onSearch={handleSearch}
            search={searchText}
            start={startDate}
            end={endDate}
          />
        </div>

        <InvoiceRedeemTable
          offset={paginationOffset(redeemResponse?.metadata)}
          data={redeems}
          path={path}
          sort={searchParams.get("sortBy")}
          onSort={handleSorting}
          onToggleApprove={handleToggleApprove}
        />

        {redeemResponse?.metadata?.totalPages > 1 ? (
          <div className="py-2 flex justify-end my-6">
            <PaginatedView
              path={`/invoice/redeem/page`}
              query={queryParams}
              total={redeemResponse?.metadata.totalPages}
              current={redeemResponse?.metadata?.currentPage}
              next={redeemResponse?.metadata?.nextPage}
              previous={redeemResponse?.metadata?.previousPage}
            />
          </div>
        ) : null}
      </div>
    </>
  );
}

export default InvoiceRedeem;
